import { getEmailTemplateById, orUndefined } from '@lms/api-client/backend';
import { LoaderCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { MjmlPreview } from '@/features/email/components/mjml-preview';
import { NotFound } from '@/routes/not-found';

// Ports app/[brand]/admin/email/[id]/page.tsx. The Next server component awaited
// the current brand and the template and used `notFound()` on a bad id; here the
// id comes from `useParams`, the template loads via SWR, the brand comes from
// `useBrand`, and a missing template renders <NotFound />. The section is
// super-admin only. Renders content inside the admin Shell.
export function EmailDetail() {
  const { brand: site, slug, loading } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const { id: idParam } = useParams<{ id: string }>();

  const id = Number.parseInt(idParam ?? '', 10);
  const validId = !Number.isNaN(id);
  const isSuperAdmin = access?.superAdmin ?? false;

  const { data: template, isLoading: templateLoading } = useSWR(
    validId && isSuperAdmin ? ['admin/email-template', id] : null,
    () => orUndefined(getEmailTemplateById(id)),
  );

  if (loading || roleLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <NotFound />;
  }

  if (!validId) {
    return <NotFound />;
  }

  if (templateLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!template) {
    return <NotFound />;
  }

  return (
    <div className="mx-auto grid w-full max-w-screen-2xl flex-1 auto-rows-max gap-4">
      <MjmlPreview template={template} site={site} />
    </div>
  );
}
